/**
 * @module DEV / Component - Carousel Component
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

import { Component, OnDestroy, Input } from '@angular/core';
import { SlideComponent } from '@support/shared/widgets/components/slide/slide.component';

export enum Direction {
  UNKNOWN,
  NEXT,
  PREV,
}

@Component({
  selector: 'tmo-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: [
    '../../../../../../style-sheets/components/carousel.component.scss',
    '../../../../../../style-sheets/components/gallery.component.scss',
  ],
})
export class CarouselComponent implements OnDestroy {
  /**
   * Bind value to show from component parameters
   */
  @Input() public itemsToShow: number;
  /**
   * Bind boolean to set autoplay
   */
  @Input() public autoPlay: boolean;
  /**
   * Bind boolean to set the loop of carousel
   */
  @Input() public loop: boolean;
  /**
   * Bind boolean to set puase on hover or not
   */
  @Input() public noPause: boolean;
  /**
   * Transition @Todo: create a set of CSS transitions
   */
  @Input() public noTransition: boolean;
  /**
   * Method to get the interval value
   */
  @Input() public get interval(): number {
    return this.theInterval;
  }
  /**
   * Boolean flag to support multiple Items
   */
  public mulitpleItems: boolean;
  /**
   * Array pf objects to holde the slides components
   */
  public slides: SlideComponent[];
  /**
   * Variable number to hold the current interval
   */
  private currentInterval: number;
  /**
   * Boolean to know if the Carousel is playing or not
   */
  private isPlaying: boolean;
  /**
   * Boolean to destroy slides @ToDo: Build the functionality
   */
  private destroyed: boolean;
  /**
   * Variable to hold the current slide ( Slide Component )
   */
  private currentSlide: SlideComponent;
  /**
   * Var to hold the interval
   */
  private theInterval: number;
  /**
   * Constructor
   */
  public constructor() {
    this.itemsToShow = 1;
    this.mulitpleItems = false;
    this.slides = [];
    this.destroyed = false;
  }
  /**
   * Unsubscribes for all subscriptions
   */
  public ngOnDestroy(): void {}
  /**
   * Boolean to destroy slides @ToDo: Build the functionality
   */
  public set interval(value: number) {
    this.theInterval = value;
    this.restartTimer();
  }
  /**
   * Method to pause the carousel
   */
  public pause(): void {
    if (!this.noPause) {
      this.isPlaying = false;
      this.resetTimer();
    }
  }
  /**
   * Method to plat the carousel
   */
  public play(): void {
    if (!this.isPlaying && this.autoPlay) {
      this.isPlaying = true;
      this.restartTimer();
    }
  }
  /**
   * Method to add a slide to the Carousel queue
   */
  public addSlide(slide: SlideComponent): void {
    slide.index = this.slides.length;
    this.slides.push(slide);
    if (this.slides.length === 1 || slide.active) {
      this.select(this.slides[this.slides.length - 1]);
      this.play();
    } else {
      if (slide.index < this.itemsToShow) {
        this.mulitpleItems = true;
        slide.active = true;
      } else {
        slide.active = false;
      }
    }
  }
  /**
   * Method to actually select the current slide to display
   */
  public select(nextSlide: SlideComponent, direction: Direction = Direction.UNKNOWN): void {
    const nextIndex = nextSlide.index;
    const directed = direction === Direction.UNKNOWN;
    if (direction && directed) {
      direction = nextIndex > this.getCurrentIndex() ? Direction.NEXT : Direction.PREV;
    }
    // Prevent this user-triggered transition from occurring if there is already one in progress
    if (nextSlide && nextSlide !== this.currentSlide) {
      this.goNext(nextSlide, direction);
    }
  }
  /**
   * Click or trigger method to move to the next slide
   */
  public next(): void {
    let newIndex = (this.getCurrentIndex() + this.itemsToShow) % this.slides.length;
    if (this.getCurrentIndex() + this.itemsToShow > this.slides.length) {
      newIndex = 0;
    }
    if (newIndex && newIndex === 0 && this.loop) {
      this.pause();
      return;
    }
    // Select is actual who select the slide to show
    this.select(this.getSlideByIndex(newIndex), Direction.NEXT);
  }
  /**
   * Click or trigger method to move to the prev slide
   */
  public prev(): void {
    // new index is previus than selected one
    const newIndex =
      this.getCurrentIndex() - this.itemsToShow < 0
        ? this.getLastItemIndex()
        : this.getCurrentIndex() - this.itemsToShow;
    // pause if loop is not active
    if (this.loop && newIndex === this.slides.length - 1) {
      this.pause();
      return;
    }
    // return the seleted items
    this.select(this.getSlideByIndex(newIndex), Direction.PREV);
  }
  /**
   * Click or trigger method to move to the next slide
   */
  private goNext(slide: SlideComponent, direction: Direction): void {
    if (this.destroyed) {
      return;
    }
    slide.direction = direction;
    slide.active = true;
    if (this.currentSlide) {
      this.currentSlide.direction = direction;
      this.currentSlide.active = false;
      //  Show extra items and hide extra items
      if (this.mulitpleItems) {
        this.selectItemsToShow(slide);
      }
    }
    this.currentSlide = slide; // Mark the prev or next Slide as Current slide
    this.restartTimer(); // every time you change slides, reset the timer
  }
  /**
   * Select a Slide or Item to show
   */
  private selectItemsToShow(slide: SlideComponent): void {
    for (let i = 1; i < this.itemsToShow; i++) {
      if (this.slides[this.currentSlide.index + i]) {
        this.slides[this.currentSlide.index + i].active = false;
      }
      if (this.slides[slide.index + i]) {
        this.slides[slide.index + i].active = true;
      }
    }
  }
  /**
   * Method to know what is the index of any slide
   */
  private getSlideByIndex(index: number): SlideComponent {
    const len = this.slides.length;
    for (let i = 0; i < len; ++i) {
      if (this.slides[i].index === index) {
        return this.slides[i];
      }
    }
  }
  /**
   * Method to know what is the current Index of the current slide
   */
  private getCurrentIndex(): number {
    return !this.currentSlide ? 0 : this.currentSlide.index;
  }
  /**
   * Method to know what is the index of any slide
   */
  private getLastItemIndex(): number {
    // Check what is the accuarte last item based on itemsToShow
    for (let i = 1; i <= this.itemsToShow; i++) {
      const slide = this.slides[this.slides.length - i].index;
      if (Number.isInteger(slide / this.itemsToShow)) {
        return slide;
      }
    }
  }
  /**
   * Method to reset the timer and then move to the next/prev slides
   */
  private restartTimer(): void {
    this.resetTimer();
    const interval: number = +this.interval;
    if (!isNaN(interval) && interval > 0) {
      const win = window;
      this.currentInterval = win.setInterval(() => {
        const nInterval = +this.interval;
        if (
          this.isPlaying &&
          !isNaN(this.interval) &&
          nInterval > 0 &&
          this.slides.length &&
          this.autoPlay
        ) {
          this.next();
        } else {
          this.pause();
        }
      }, interval);
    }
  }
  /**
   * Method to reset the timer
   */
  private resetTimer(): void {
    if (this.currentInterval) {
      clearInterval(this.currentInterval);
      this.currentInterval = null;
    }
  }
}
